import React from 'react';
import { MathJax } from 'better-react-mathjax';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';

const MarkdownWithMath = ({ content, onModalLinkClick }) => {
  if (typeof content !== 'string') {
    console.warn("❌ MarkdownWithMath received non-string content:", content);
    return null;
  }
  return (
    <MathJax dynamic>
       
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        
        components={{
          a: ({ href, children }) => {
            // ✅ Modal link handler
            console.log("🔗 href:", href);
            console.log("📦 Raw content:", content);

            if (href?.startsWith('#/custom-modal:/')) {
              
              const [id, title] = href.replace('#/custom-modal:/', '').split('|');
            
              console.log('✅ Modal link detected:', id, title); // Step 1: check parsing
            
              return (
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    onModalLinkClick?.(id);
                  }}
                  className="text-indigo-600 underline cursor-pointer relative group"
                >
                  {children}
                  {title && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-50 whitespace-nowrap pointer-events-none">
                      Quick view: <strong>{title}</strong>
                    </div>
                  )}
                </span>
              );
                      }
                    
            // ✅ Clarify link (goes to new page)
            if (href?.startsWith('clarify:')) {
              const [id, title] = href.replace('clarify:', '').split('|');
        
              return (
                <span className="relative group inline-block cursor-pointer">
                  <Link
                    to={`/clarify/${id}`}
                    className="text-indigo-600 font-medium hover:underline"
                  >
                    {children}
                  </Link>
                  {title && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-50 whitespace-nowrap pointer-events-none">
                      Know what <strong>{title}</strong> is?
                    </div>
                  )}
                </span>
              );
            }
        
            // 🔗 Default external link
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
              >
                {children}
              </a>
            );
          }
        }}
              >
        {content}
      </ReactMarkdown>
    </MathJax>
  );
};
export { MarkdownWithMath };

export const renderBlocks = (blocks, onModalLinkClick, depth = 0) => {
  if (depth > 10) {
    console.error('⚠️ renderBlocks: Max render depth exceeded');
    return null;
  }

  if (!Array.isArray(blocks)) return null;

  return blocks.map((block, idx) => {
    if (depth > 10) {
      console.error('🚨 renderBlocks: max depth exceeded');
      return <div className="text-red-600">⚠️ Failed to render due to deep nesting.</div>;
    }
    switch (block.type) {
      case 'text':
        {
          if (typeof block.value !== 'string') {
            console.warn('⛔ Skipping non-string block.value', block.value);
            return null;
          }
            // 🟡 Log the first few characters to detect nested JSON
          console.log("🔍 block.value preview:", block.value.slice(0, 100));
        
          // Detect accidentally stringified JSON
          if (block.value.trim().startsWith('[{"type":"')) {
            console.error('🚨 Skipping stringified JSON block to avoid recursion:', block.value);
            return (
              <div key={idx} className="text-red-600 text-sm">
                ⚠️ Malformed content detected — skipped to avoid crash.
              </div>
            );
          }
          return (
            <div key={idx} className="mb-2">
              <MarkdownWithMath content={block.value} onModalLinkClick={onModalLinkClick} />
            </div>
          );
        }

      case 'latex':
        return (
          
          <div key={idx} className="my-2 text-indigo-700 font-medium">
          <span key={idx} className="inline-block align-middle mr-1">
            <MarkdownWithMath content={block.value}  />
            </span>
          </div>
        );

      case 'image': {
        const width = block.width || 'auto';
        const maxWidth = block.maxWidth || '300px';
        return (
          <div key={idx} className="mt-3">
            <img
              src={block.value}
              alt="Visual"
              style={{ width, maxWidth }}
              className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg rounded shadow"
              onError={(e) => (e.target.style.display = 'none')}
            />
          </div>
        );
      }

      case 'link':
        return (
          <div key={idx} className="mt-2">
            <a
              href={block.value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-800"
            >
              {block.label || block.value}
            </a>
          </div>
        );

      case 'code':
        return (
          <pre
            key={idx}
            className="bg-gray-800 text-white text-sm p-3 rounded overflow-auto mt-2"
          >
            <code>{block.value}</code>
          </pre>
        );

      case 'video':
        return (
          <div key={idx} className="mt-4">
            <video controls className="w-full rounded">
              <source src={block.value} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case 'table': {
        const { headers = [], rows = [] } = block.value || {};
        return (
          <div key={idx} className="overflow-auto my-4">
            <table className="table-auto border-collapse border border-gray-300 text-sm text-left mr-auto">
              <thead>
                <tr>
                  {headers.map((header, i) => (
                    <th key={i} className="border border-gray-300 px-2 py-1 font-semibold bg-gray-100">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rIndex) => (
                  <tr key={rIndex}>
                    {row.map((cell, cIndex) => (
                      <td key={cIndex} className="border border-gray-300 px-2 py-1">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      default:
        return null;
    }
  });
};
